import { useEffect, useRef } from "react";

import { useAuth } from "@/auth";
import { useDisplayCurrency } from "@/features/display-currency/useDisplayCurrency";
import { PROMOTIONS_ENABLED } from "@/features/promotions/config";
import {
  SEARCH_RESULTS_QUERY_KEY,
  type SearchRoute,
  searchRouteQueryOptions,
} from "@/features/search/api/route";
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

/**
 * Results for a resolved route, whichever endpoint backs it.
 *
 * `keepPreviousData` keeps the last successful response on screen while a new one
 * loads, so filtering/paginating never blanks the UI. `isPlaceholderData` flags the
 * in-flight state; components use it to disable interaction.
 *
 * The signed-in viewer is picked up here rather than passed in, so every caller gets
 * per-user favorites without threading auth through the component tree.
 */
type UseSearchResultsOptions = {
  /**
   * WHO-5684: the dehydrated results were fetched without the viewer so the HTML could be cached
   * at the CDN. A signed-in viewer then refetches once on mount to get their hearts; an anonymous
   * viewer keeps the hydrated data, which is already theirs.
   */
  hydratedAnonymously?: boolean;
};

export const useSearchResults = (
  route: SearchRoute,
  { hydratedAnonymously = false }: UseSearchResultsOptions = {}
) => {
  const { token, userId } = useAuth();
  const queryClient = useQueryClient();

  /*
   * Cached results carry whoever fetched them, and the key deliberately doesn't (see
   * `searchRouteQueryOptions` — an auth-shaped key breaks SSR hydration). So a login or logout has
   * to retire them explicitly, or the previous viewer's hearts stay on screen for `staleTime`.
   *
   * Seeded with the current viewer so mounting is never a change: invalidating on mount would throw
   * away the state the server just hydrated and refetch it immediately. Invalidate rather than
   * remove, so results stay visible while the corrected ones load.
   */
  const lastViewerId = useRef(userId);

  useEffect(() => {
    if (lastViewerId.current === userId) {
      return;
    }

    lastViewerId.current = userId;
    queryClient.invalidateQueries({ queryKey: SEARCH_RESULTS_QUERY_KEY });
  }, [queryClient, userId]);

  const currency = useDisplayCurrency();

  const query = useQuery({
    ...searchRouteQueryOptions(route, token, undefined, currency),
    placeholderData: keepPreviousData,
    refetchOnMount:
      PROMOTIONS_ENABLED || (hydratedAnonymously && token) ? "always" : true,
  });
  const { refetch } = query;

  useEffect(() => {
    if (!PROMOTIONS_ENABLED) {
      return;
    }

    const refreshVisible = () => {
      if (document.visibilityState === "visible") {
        void refetch();
      }
    };
    // Delivery tickets expire after 30 minutes; renew them before expiry and
    // recheck campaign availability whenever the shopper returns to this tab.
    const timer = window.setInterval(refreshVisible, 15 * 60_000);
    document.addEventListener("visibilitychange", refreshVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", refreshVisible);
    };
  }, [refetch]);

  return query;
};
